import React, { createContext, useContext } from 'react';
import './Tabs.css';

/**
 * Baustein — Tabs.
 * Tab-Navigation für Modals/Seiten. Token-konform, Dark/Light.
 * Hand-rolled, weil `@radix-ui/react-tabs` nicht installiert ist: content-breit,
 * links-bündig, Accent-Underline als aktiver Indikator.
 *
 * Props (Tabs): value, onValueChange, dense, children
 * `dense` nimmt der Reiterzeile den Abstand nach unten — für eingebettete Flächen
 * (Panel, Seitenspalte), in denen der seitenbreite Abstand ein Loch reißt.
 * Sub-Components: Tabs.List, Tabs.Trigger (value, disabled, icon?), Tabs.Panel (value)
 *
 * Beispiel:
 *   <Tabs value={tab} onValueChange={setTab}>
 *     <Tabs.List>
 *       <Tabs.Trigger value="create" icon={Plus}>Neues Deck</Tabs.Trigger>
 *       <Tabs.Trigger value="import">Archidekt</Tabs.Trigger>
 *     </Tabs.List>
 *     <Tabs.Panel value="create">…</Tabs.Panel>
 *     <Tabs.Panel value="import">…</Tabs.Panel>
 *   </Tabs>
 */

const TabsCtx = createContext(null);

export default function Tabs({ value, onValueChange, dense = false, children }) {
  return (
    <div className={`tabs${dense ? ' tabs--dense' : ''}`}>
      <TabsCtx.Provider value={{ value, onValueChange }}>
        {children}
      </TabsCtx.Provider>
    </div>
  );
}

Tabs.List = function TabsList({ children }) {
  return <div className="tabs__list" role="tablist">{children}</div>;
};

Tabs.Trigger = function TabsTrigger({ value, disabled, icon: Icon, children }) {
  const ctx = useContext(TabsCtx);
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      className={`tabs__trigger${active ? ' tabs__trigger--active' : ''}`}
      onClick={() => ctx.onValueChange(value)}
    >
      {Icon && <Icon size={15} aria-hidden />}
      {children}
    </button>
  );
};

Tabs.Panel = function TabsPanel({ value, children }) {
  const ctx = useContext(TabsCtx);
  if (ctx.value !== value) return null;
  return (
    <div className="tabs__panel" role="tabpanel">
      {children}
    </div>
  );
};
